<template>
  <v-card flat>
    <div v-if="isLoading" class="loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div :class="{ 'is-loading': isLoading }">
      <div v-if="hasTSILinkSubs" data-cy="userProfileSubscriptionsWebsite">
        <div class="text-subtitle-1 font-weight-bold">
          {{ $t('accountSubscriptions.TSISubscriptions') }}
        </div>
        <v-list>
          <template v-if="hasSubscriptions.basic">
            <v-list-item v-for="sub in basicSubscriptions" :key="sub.id" lines="two">
              <v-list-item-title>{{ sub.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('accountSubscriptions.deviceAllowance') }}
                {{
                  $t('accountSubscriptions.deviceSubscriptionsUsedRemaining', {
                    qty: sub.totalUsed,
                    remaining: sub.totalQtyRemaining
                  })
                }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
          <template v-if="hasSubscriptions.premium">
            <v-list-item v-for="sub in premiumSubscriptions" :key="sub.id" lines="two">
              <v-list-item-title>{{ sub.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('accountSubscriptions.deviceAllowance') }}
                {{
                  $t('accountSubscriptions.deviceSubscriptionsUsedRemaining', {
                    qty: sub.totalUsed,
                    remaining: sub.totalQtyRemaining
                  })
                }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
          <template v-if="hasSubscriptions.genericSubsWithoutSmartBridgeDeviceFee">
            <v-list-item
              v-for="sub in genericSubsWithoutSmartBridgeDeviceFee"
              :key="sub.id"
              :data-cy="sub.id"
              lines="one"
            >
              <v-list-item-title>{{ sub.name }}</v-list-item-title>
            </v-list-item>
          </template>
          <template v-if="hasSubscriptions.smartBridgeDeviceFeeSub">
            <v-list-item
              v-for="sub in smartBridgeDeviceFeeSub"
              :key="sub.id"
              :data-cy="sub.id"
              lines="two"
            >
              <v-list-item-title>{{ sub.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('accountSubscriptions.deviceAllowance') }}
                {{
                  $t('accountSubscriptions.deviceSubscriptionsUsedRemaining', {
                    qty: sub.totalUsed,
                    remaining: sub.totalQtyRemaining
                  })
                }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-list>
      </div>
      <div v-if="hasSubscriptions.dataServices" data-cy="userProfileSubscriptionsDataServices">
        <div class="text-subtitle-1 font-weight-bold">
          {{ $t('accountSubscriptions.dataServicesSubscriptions') }}
        </div>
        <v-list>
          <v-list-item v-for="sub in dataSubscriptions" :key="sub.id" class="less-top" lines="two">
            <v-list-item-title>{{ sub.name }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ $t('accountSubscriptions.deviceAllowance') }}
              {{
                $t('accountSubscriptions.deviceSubscriptionsUsedRemaining', {
                  qty: sub.totalUsed,
                  remaining: sub.totalQtyRemaining
                })
              }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { subscriptionTiers } from '@/store/subscriptions/utils'
import { NAMESPACES } from '@/store/types'
import { SUBSCRIPTIONS_GETTERS, SUBSCRIPTIONS_ACTIONS } from '@/store/subscriptions'
import { DEVICE_ACTIONS } from '@/store/devices'
import type { EnrichedSubscription } from '@/store/subscriptions/types'

const TSILINK_ADD_ON_SMARTBRIDGE_DEVICE_FEE =
  subscriptionTiers.TSILINK_ADD_ON_SMARTBRIDGE_DEVICE_FEE

export default defineComponent({
  name: 'AccountSubscriptions',
  async mounted() {
    await this.getSubscriptions()
  },
  data() {
    return {
      isLoading: false as boolean
    }
  },
  computed: {
    genericTSILinkSubs(): EnrichedSubscription[] {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_GENERIC_TSI_LINK}`
      ]
    },
    genericSubsWithoutSmartBridgeDeviceFee(): EnrichedSubscription[] {
      return this.genericTSILinkSubs.filter(
        (sub) => sub.id !== TSILINK_ADD_ON_SMARTBRIDGE_DEVICE_FEE
      )
    },
    smartBridgeDeviceFeeSub(): EnrichedSubscription[] {
      return this.genericTSILinkSubs.filter(
        (sub) => sub.id === TSILINK_ADD_ON_SMARTBRIDGE_DEVICE_FEE
      )
    },
    basicSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters[`${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_BASIC}`]
    },
    premiumSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters[`${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_PREMIUM}`]
    },
    dataSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DATA_SERVICES}`
      ]
    },
    hasSubscriptions(): Record<string, boolean> {
      return {
        basic: this.basicSubscriptions.length > 0,
        premium: this.premiumSubscriptions.length > 0,
        dataServices: this.dataSubscriptions.length > 0,
        genericSubsWithoutSmartBridgeDeviceFee:
          this.genericSubsWithoutSmartBridgeDeviceFee.length > 0,
        smartBridgeDeviceFeeSub: this.smartBridgeDeviceFeeSub.length > 0
      }
    },
    hasTSILinkSubs(): boolean {
      return (
        this.hasSubscriptions.basic ||
        this.hasSubscriptions.premium ||
        this.hasSubscriptions.genericSubsWithoutSmartBridgeDeviceFee ||
        this.hasSubscriptions.smartBridgeDeviceFeeSub
      )
    }
  },
  methods: {
    async getSubscriptions(): Promise<void> {
      this.isLoading = true
      if (!this.$store.state[NAMESPACES.DEVICES]?.allDevices) {
        await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
      }
      await this.$store.dispatch(
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
        { auth: this.$auth, api: this.$api }
      )
      this.isLoading = false
    }
  }
})
</script>

<style lang="scss" scoped>
.is-loading {
  opacity: 0.2;
}
.loading-container {
  position: absolute;
  top: 200px;
  left: calc(50% - 64px);
  opacity: 1;
}
</style>
